import React from "react";
import styled from "styled-components";
import fowardWhite from "../../assets/fowardWhite.png";
import { formatDate } from "../../utils/formatters";

const Container = styled.div`
  padding: 8px;
  max-width: 700px;
  width: 100%;
  margin-left: 60px;
  margin-top: 10px;
  box-sizing: border-box;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: 16px;
  color: #ff9f1f;
  font-weight: 600;
`;

const Subtitle = styled.span`
  display: block;
  font-size: 12px;
  color: #002e3b;
  margin-top: 4px;
`;

const Section = styled.div`
  margin-bottom: 8px;
  margin-left: 10px;
  width: 100%;
  box-sizing: border-box;
`;

const Div = styled.div`
  width: 100%;
  margin-bottom: 20px;
  box-sizing: border-box;
`;

const HighlightBox = styled.div`
  border: 1px solid #ff9f1f;
  border-radius: 10px;
  padding: 10px 18px 0px;
  margin-bottom: 10px;
  width: 100%;
  box-sizing: border-box;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: ${({ columns }) => columns || "1fr 1fr"};
  width: 100%;
  box-sizing: border-box;
  margin-bottom: 10px;
  gap: 0 16px;
`;

const Column = styled.div`
  min-width: 0;
  box-sizing: border-box;
`;

const Label = styled.div`
  font-size: 13px;
  font-weight: bold;
  color: #002e3b;
`;

const Value = styled.div`
  font-size: 14px;
  color: #4a4a4a;
  margin-top: 6px;
  margin-bottom: 4px;
  white-space: pre-line;
  word-break: break-word;
`;

const DaysRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
`;

const DayItem = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #002e3b;
`;

const DayMarker = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid #6ab8a8;
  background-color: ${({ checked }) => (checked ? "#6ab8a8" : "transparent")};
  box-sizing: border-box;
`;

const ButtonsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 32px;
  margin-bottom: 32px;
  width: 100%;
`;

const BackButton = styled.button`
  background-color: #999999;
  color: #ffffff;
  font-size: 15px;
  font-weight: 600;
  padding: 8px 20px;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.223);
  &:hover {
    background-color: #777777;
  }
`;

const SubmitButton = styled.button`
  padding: 8px 20px;
  background-color: #6ab8a8;
  color: #ffffff;
  border: none;
  border-radius: 9px;
  cursor: pointer;
  font-size: 15px;
  min-width: 40px;
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.223);
  display: flex;
  align-items: center;
  gap: 8px;
  &:hover {
    background-color: #56a998ff;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const FileName = styled.div`
  font-size: 12px;
  color: #002e3b;
  margin-top: 6px;
  word-break: break-all;
`;

const mapTipoAtividadeToLabel = (tipoAtividade) => {
  switch (tipoAtividade) {
    case "ESTAGIO_SUPERVISIONADO": return "Estágio Supervisionado";
    case "ESTAGIO_OBRIGATORIO":    return "Estágio Obrigatório";
    case "INTERNATO_MEDICO":       return "Internato Médico";
    case "AULA_PRATICA":           return "Aula Prática";
    default:                       return tipoAtividade || "-";
  }
};

const SolicitarConfirm = ({
  data,
  cursoNome,
  diasSemana,
  planoEnsinoFileName,
  onBack,
  onSubmit,
  submitting,
}) => {
  const {
    tipoAtividade, coordCursoNome, coordCursoEmail,
    coordEstagioNome, coordEstagioEmail, setor, disciplina,
    fase, qtdAlunos, horasPorAluno, dataInicio, dataFim,
    turno, horaInicio, horaFim, docenteResponsavel, cpfDocenteResponsavel,
    conselhoClasse, objetivo,
  } = data || {};

  return (
    <Container>
      <HeaderRow>
        <div>
          <Title>Confirme os dados</Title>
          <Subtitle>
            Verifique se todas as informações estão corretas antes de enviar a solicitação.
          </Subtitle>
        </div>
      </HeaderRow>

      <HighlightBox>
        <Row>
          <Column>
            <Label>Tipo de atividade*</Label>
            <Value>{mapTipoAtividadeToLabel(tipoAtividade)}</Value>
          </Column>
          <Column>
            <Label>Curso*</Label>
            <Value>{cursoNome || "-"}</Value>
          </Column>
        </Row>

        <Row>
          <Column>
            <Label>Nome do coordenador do curso*</Label>
            <Value>{coordCursoNome || "-"}</Value>
          </Column>
          <Column>
            <Label>E-mail do coordenador do curso*</Label>
            <Value>{coordCursoEmail || "-"}</Value>
          </Column>
        </Row>

        <Row>
          <Column>
            <Label>Nome do coordenador de estágio*</Label>
            <Value>{coordEstagioNome || "-"}</Value>
          </Column>
          <Column>
            <Label>E-mail do coordenador de estágio*</Label>
            <Value>{coordEstagioEmail || "-"}</Value>
          </Column>
        </Row>
      </HighlightBox>

      <Section>
        <Row columns="2fr 2fr 1fr">
          <Column>
            <Label>Setor</Label>
            <Value>{setor || "-"}</Value>
          </Column>
          <Column>
            <Label>Nome da disciplina</Label>
            <Value>{disciplina || "-"}</Value>
          </Column>
          <Column>
            <Label>Fase</Label>
            <Value>{fase ? `${fase}ª` : "-"}</Value>
          </Column>
        </Row>

        <Row columns="2fr 2fr 1fr">
          <Column>
            <Label>Quantidade de alunos</Label>
            <Value>{qtdAlunos || "-"}</Value>
          </Column>
          <Column>
            <Label>Nº total de horas p/ aluno</Label>
            <Value>{horasPorAluno || "-"}</Value>
          </Column>
        </Row>

        <Row columns="2fr 2fr 1fr">
          <Column>
            <Label>Data de início do estágio</Label>
            <Value>{formatDate(dataInicio) || "-"}</Value>
          </Column>
          <Column>
            <Label>Data de fim do estágio</Label>
            <Value>{formatDate(dataFim) || "-"}</Value>
          </Column>
        </Row>

        <Row columns="2fr 2fr 1fr">
          <Column>
            <Label>Turno</Label>
            <Value>{turno || "-"}</Value>
          </Column>
          <Column>
            <Label>Horário Início</Label>
            <Value>{horaInicio || "-"}</Value>
          </Column>
          <Column>
            <Label>Horário Término</Label>
            <Value>{horaFim || "-"}</Value>
          </Column>
        </Row>

         <Div>
           <Label>Dias da semana</Label>
           <DaysRow>
             {[
               "Segunda",
               "Terça",
               "Quarta",
               "Quinta",
               "Sexta",
               "Sábado",
               "Domingo",
            ].map((dia) => {
               const marcado = Boolean(diasSemana && diasSemana.includes(dia));
               return (
                 <DayItem key={dia}>
                   <DayMarker checked={marcado} />
                   {dia}
                 </DayItem>
               );
             })}
           </DaysRow>
         </Div>

        <Row columns="2fr 2fr 1fr">
          <Column>
            <Label>Docente responsável</Label>
            <Value>{docenteResponsavel || "-"}</Value>
          </Column>
          <Column>
            <Label>CPF do docente responsável</Label>
            <Value>{cpfDocenteResponsavel || "-"}</Value>
          </Column>
          <Column>
            <Label>Sigla e número do conselho de Classe</Label>
            <Value>{conselhoClasse || "-"}</Value>
          </Column>
        </Row>

        <Div>
          <Label>Objetivo do estágio</Label>
          <Value>{objetivo || "-"}</Value>
        </Div>
      </Section>

      <Section>
        <Label>Anexo: Plano de Ensino</Label>
        <Value>
          {planoEnsinoFileName
            ? <FileName>{planoEnsinoFileName}</FileName>
            : "Nenhum arquivo selecionado."}
        </Value>
      </Section>

      <ButtonsRow>
        <BackButton type="button" onClick={onBack}>Voltar</BackButton>
        <SubmitButton type="button" onClick={onSubmit} disabled={submitting}>
          {submitting ? "Enviando..." : "Enviar"}
          <img src={fowardWhite} alt="Enviar" width="18" />
        </SubmitButton>
      </ButtonsRow>
    </Container>
  );
};

export default SolicitarConfirm;